
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { OCR } from '../services/api';
import Tesseract from 'tesseract.js';
import { useAuth } from '../context/AuthContext';

const EMPTY_FIELDS = { name: '', designation: '', company: '', number: '', email: '' };

export default function OCRScanner() {
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const fileRef     = useRef(null);
  const videoRef    = useRef(null);
  const canvasRef   = useRef(null);

  const [mode, setMode]         = useState('idle');       // idle | camera | scanning | result | saving
  const [preview, setPreview]   = useState('');
  const [stream, setStream]     = useState(null);
  const [fields, setFields]     = useState(EMPTY_FIELDS);
  const [scanId, setScanId]     = useState(null);
  const [err, setErr]           = useState('');

  // ── Camera ─────────────────────────────────────────────────
  const startCamera = async () => {
    setErr('');
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 } },
      });
      setStream(s);
      setMode('camera');
      // Attach stream after render
      requestAnimationFrame(() => {
        if (videoRef.current) videoRef.current.srcObject = s;
      });
    } catch {
      setErr('Camera not available. Please upload an image instead.');
    }
  };

  const stopCamera = () => {
    stream?.getTracks().forEach(t => t.stop());
    setStream(null);
  };

  const capture = () => {
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setPreview(dataUrl);
    stopCamera();
    runOCR(dataUrl.split(',')[1]);
  };

  // ── File upload ─────────────────────────────────────────────
  // const onFile = e => {
  //   const file = e.target.files?.[0];
  //   if (!file) return;
  //   const reader = new FileReader();
  //   reader.onload = ev => {
  //     const dataUrl = ev.target.result;
  //     setPreview(dataUrl);
  //     runOCR(dataUrl.split(',')[1]);
  //   };
  //   reader.readAsDataURL(file);
  //   // reset so same file can be re-selected
  //   e.target.value = '';
  // };

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

      const result = await Tesseract.recognize(
          file,
          "eng"
      );
      const text = result.data.text;
      
      const values = result.data.text
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);

    
      
      const reader = new FileReader();
        reader.onload = ev => {
          const dataUrl = ev.target.result;
          setPreview(dataUrl);
          runOCR(values);
          //runOCR(text.split(',')[1]);
      };
      reader.readAsDataURL(file);
      setMode('result');
  };

  // ── OCR call ────────────────────────────────────────────────
  const runOCR = async (b64) => {
    setMode('scanning'); setErr('');
    try {
      //const res = await OCR.scan(b64);
      const res = b64;
      setScanId(res.scan_id);
      setFields({
        name:        res[0]      || '',
        designation: res[1] || '',
        company:     res[2]     || '',
        number:       res[3]       || '',
        email:       res[4]       || '',
      });
      setMode('result');
    } catch (ex) {
      setErr('OCR failed: ' + ex.message);
      setMode('idle');
    }
  };

  // ── Save as customer ─────────────────────────────────────────
  const saveCustomer = async () => {
    setMode('saving');
    try {
      await OCR.saveAsCustomer({ scan_id: scanId, ...fields, corp_id: user.corp_id, user_id: user.id });
      navigate('/customers');
    } catch (ex) {
      setErr('Save failed: ' + ex.message);
      setMode('result');
    }
  };

  const reset = () => { setMode('idle'); setPreview(''); setFields(EMPTY_FIELDS); setErr(''); setScanId(null); };

  const setF = k => e => setFields(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="page" style={{ maxWidth: 720 }}>
      <div className="page-header">
        <div className="page-title">📷 Business Card Scanner</div>
      </div>
      <p style={{ color: 'var(--muted)', marginBottom: '1.5rem', fontSize: '.9rem' }}>
        Capture a business card to automatically create a customer record.
      </p>

      {err && <div className="alert alert-error">{err}</div>}

      {/* ── IDLE ── */}
      {mode === 'idle' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', maxWidth: 420 }}>
          <button onClick={startCamera} style={btnStyle}>
            <span style={{ fontSize: '2.2rem' }}>📸</span>
            <span style={{ fontWeight: 600 }}>Take Photo</span>
            <small style={{ color: 'var(--faint)' }}>Use camera</small>
          </button>
          <button onClick={() => fileRef.current.click()} style={btnStyle}>
            <span style={{ fontSize: '2.2rem' }}>📁</span>
            <span style={{ fontWeight: 600 }}>Upload Image</span>
            <small style={{ color: 'var(--faint)' }}>JPG / PNG</small>
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
        </div>
      )}

      {/* ── CAMERA ── */}
      {mode === 'camera' && (
        <div style={{ position: 'relative', maxWidth: 480 }}>
          <video ref={videoRef} autoPlay playsInline
            style={{ width: '100%', borderRadius: 12, background: '#000' }} />
          <canvas ref={canvasRef} hidden />
          {/* Card guide overlay */}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ width: '85%', height: '54%', border: '2px solid var(--accent)', borderRadius: 8,
              boxShadow: '0 0 0 2000px rgba(0,0,0,.45)' }} />
            <p style={{ color: '#fff', fontSize: '.82rem', marginTop: '.5rem', textShadow: '0 1px 4px #000' }}>
              Align card within frame
            </p>
          </div>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginTop: '1rem' }}>
            <button className="btn btn-primary" style={{ borderRadius: 50, padding: '.7rem 2rem', fontSize: '1rem' }}
              onClick={capture}>📸 Capture</button>
            <button className="btn btn-ghost" onClick={() => { stopCamera(); setMode('idle'); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── SCANNING ── */}
      {mode === 'scanning' && (
        <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          {preview && <img src={preview} alt="card" style={{ width: 200, borderRadius: 8, marginBottom: '1rem' }} />}
          <div className="spinner" style={{ margin: '0 auto 1rem' }} />
          <p style={{ color: 'var(--muted)' }}>Extracting text from card…</p>
        </div>
      )}

      {/* ── RESULT ── */}
      {mode === 'result' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '1.5rem', alignItems: 'start' }}>
          {preview && (
            <img src={preview} alt="scanned card"
              style={{ width: '100%', borderRadius: 10, border: '1px solid var(--border)' }} />
          )}
          <div className="card">
            <div style={{ fontWeight: 600, marginBottom: '.3rem' }}>Extracted Information</div>
            <p style={{ color: 'var(--muted)', fontSize: '.8rem', marginBottom: '1rem' }}>
              Review and correct before saving:
            </p>
            {[
              { k: 'name',        icon: '👤', label: 'Name'        },
              { k: 'company',     icon: '🏢', label: 'Company'     },
              { k: 'designation', icon: '💼', label: 'Designation' },
              { k: 'number',       icon: '📞', label: 'Phone'       },
              { k: 'email',       icon: '✉️',  label: 'Email'       },
            ].map(({ k, icon, label }) => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.7rem' }}>
                <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{icon}</span>
                <label style={{ flex: 1 }}>
                  <span style={{ fontSize: '.72rem', marginBottom: 2 }}>{label}</span>
                  <input value={fields[k]} onChange={setF(k)} placeholder={`Enter ${label.toLowerCase()}…`} />
                </label>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '.8rem', marginTop: '1rem' }}>
              <button className="btn btn-success" style={{ flex: 1 }} onClick={saveCustomer}>
                ✓ Save as Customer
              </button>
              <button className="btn btn-ghost" onClick={reset}>Discard</button>
            </div>
          </div>
        </div>
      )}

      {/* ── SAVING ── */}
      {mode === 'saving' && (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <div className="spinner" style={{ margin: '0 auto 1rem' }} />
          <p style={{ color: 'var(--muted)' }}>Saving customer…</p>
        </div>
      )}
    </div>
  );
}

const btnStyle = {
  background: 'var(--surface)', border: '2px dashed var(--border)', borderRadius: 12,
  padding: '1.8rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center',
  gap: '.4rem', cursor: 'pointer', transition: 'border-color .15s',
};
